import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { getCurrencySymbol } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { absMoneyList } from '@/features/dashboard/bookingChanges/utils/money.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

/**
 * Formatowanie tekstów, które model widoku niesie już jako string — daty
 * wierszy, zakres turnusu, dopiski pod kwotami kafli.
 *
 * Kwoty w polach `amount` zostają listami `MoneyValueDto` i renderuje je
 * `MoneyAmountList`; tutaj formatujemy tylko te miejsca, w których kontrakt
 * modelu widoku wymusza gotowe zdanie (`amountNote`, `settlementDescription`,
 * `meta` kafla). Zdanie o kwocie wielowalutowej wymienia obie waluty
 * (`formatMoneyList`) — wybranie jednej z nich byłoby kłamstwem.
 */

/**
 * Daty modułu mieszkają w `@/utils/dateFormatters.ts` — projekt wymusza tu
 * zapis `DD MMM YYYY` („09 lis 2019”), ten sam, którym mówi reszta aplikacji
 * przez `timeUtils`. Zostają tutaj pod skróconymi nazwami, bo tak nazywa je
 * cały moduł; implementacja jest jedna i wspólna.
 */
export {
  formatDayMonthYear as formatDate,
  formatDayMonth as formatDayAndMonth,
  formatDayMonthTime as formatDateTime,
  formatDayMonthYearTime as formatFullDateTime,
  formatDayMonthYearRange as formatDateRange,
} from '@/utils/dateFormatters.ts';

export const currencySuffix = (currency: Currency): string =>
  getCurrencySymbol(currency);

/** Kwoty w różnych walutach stoją obok siebie, nigdy się nie dodają. */
const MONEY_LIST_SEPARATOR = ' + ';

/**
 * Kwoty ze znakiem rozdziela kropka, a nie plus: „+ 200,00 zł + − 33,00 €”
 * czyta się jak działanie, którym nie jest.
 */
const SIGNED_MONEY_SEPARATOR = ' · ';

/**
 * Kwota bez groszy, używana w dopiskach kafli („1 800 → 1 960 zł”). Grosze
 * pokazujemy tylko wtedy, gdy są niezerowe.
 */
export const formatMoneyCompact = (
  value: MoneyValueDto,
  withCurrency = true,
): string => {
  const hasFraction = Math.abs(value.amount % 1) > 0;
  const formatted = new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value.amount);

  return withCurrency
    ? `${formatted} ${currencySuffix(value.currency)}`
    : formatted;
};

/**
 * Kwota z groszami i symbolem waluty („1 250,00 zł”) — do zdań opisowych,
 * w których model widoku wymaga gotowego tekstu, a nie `MoneyValueDto`.
 *
 * Deleguje do wspólnego `formatPrice`, żeby zdanie opisowe i kwota w tabeli
 * obok nigdy nie rozjechały się zapisem.
 */
export const formatMoneyExact = (value: MoneyValueDto): string => {
  // Minus typograficzny ze spacją, jak w kolumnie „Wpływ” (`formatSignedPrice`)
  // — `formatPrice` stawia łącznik, który obok niej wygląda jak inny zapis.
  const formatted = formatPrice(Math.abs(value.amount), value.currency);
  return value.amount < 0 ? `− ${formatted}` : formatted;
};

/**
 * Lista kwot jako jeden napis: „200,00 zł + 333,00 €”. Ten sam separator, co
 * w `formatPriceList` całej aplikacji. Pusta lista to **brak kwoty**, nie zero.
 */
export const formatMoneyList = (values: MoneyValueDto[]): string =>
  values.length === 0
    ? EMPTY_VALUE
    : values.map(formatMoneyExact).join(MONEY_LIST_SEPARATOR);

/** Lista kwot bez znaku — kierunek niesie sam napis („Dopłata”, „Zwrot”). */
export const formatAbsoluteMoneyList = (values: MoneyValueDto[]): string =>
  formatMoneyList(absMoneyList(values));

export const formatMoneyCompactList = (values: MoneyValueDto[]): string =>
  values.length === 0
    ? EMPTY_VALUE
    : values
        .map((value) => formatMoneyCompact(value))
        .join(MONEY_LIST_SEPARATOR);

/**
 * `1 800 → 1 960 zł` — dopisek pod kwotą kafla osoby w trybie edycji.
 *
 * Symbol waluty pomijamy po lewej stronie strzałki tylko wtedy, gdy obie strony
 * mówią o tej samej, jedynej walucie; przy dwóch walutach każda liczba musi
 * powiedzieć, o którą chodzi.
 */
export const formatMoneyTransition = (
  before: MoneyValueDto[],
  after: MoneyValueDto[],
): string => {
  const single =
    before.length === 1 &&
    after.length === 1 &&
    before[0].currency === after[0].currency;

  const left = single
    ? formatMoneyCompact(before[0], false)
    : formatMoneyCompactList(before);

  return `${left} → ${formatMoneyCompactList(after)}`;
};

/**
 * Kwota ze znakiem — delta szkicu w stopce rozliczenia i na kaflu osoby.
 * Zero zostaje bez znaku, bo „bez wpływu na cenę" to nie jest dopłata.
 */
export const formatSignedPrice = (value: MoneyValueDto): string => {
  const formatted = formatPrice(Math.abs(value.amount), value.currency);

  if (value.amount > 0) {
    return `+ ${formatted}`;
  }
  if (value.amount < 0) {
    return `− ${formatted}`;
  }
  return formatted;
};

/**
 * Lista kwot ze znakiem jako jeden napis — do miejsc, w których kwota jest
 * tekstem zdania albo paska, a nie osobnym elementem (`MoneyAmountList`).
 */
export const formatSignedMoneyList = (values: MoneyValueDto[]): string =>
  values.length === 0
    ? EMPTY_VALUE
    : values.map(formatSignedPrice).join(SIGNED_MONEY_SEPARATOR);
